import { NavLink, Redirect, useLocation } from "react-router-dom";
import {
  getLandingForLang,
  landingPages,
  LandingFaq,
} from "../content/landingPages";
import Seo from "./Seo";
import LiveSalesShowcase from "./LiveSalesShowcase";
import TanksShowcase from "./TanksShowcase";
import OrderSuggestionsShowcase from "./OrderSuggestionsShowcase";
import Testimonials from "./testimonials/Testimonials";
import styles from "./landingPage.module.css";

const BASE_URL = "https://shifttracker.net";
// Cornerstone TURPAK article, cross-linked below (slug differs per language).
const TURPAK_TR = "turpak-otomasyonu-banka-mutabakati";
const TURPAK_EN = "turpak-automation-vs-bank-reconciliation";

const UI = {
  tr: { related: "İlgili sayfalar" },
  en: { related: "Related pages" },
};

function LandingPage(): JSX.Element {
  const location = useLocation();
  const isEnglish =
    location.pathname === "/en" || location.pathname.startsWith("/en/");
  const lang: "tr" | "en" = isEnglish ? "en" : "tr";
  const prefix = isEnglish ? "/en" : "";

  // Strip the /en prefix, the leading slash AND any trailing slash; Netlify
  // serves these pages at the trailing-slash URL (/benzin-istasyonu-yazilimi/),
  // so without this the slug wouldn't match and the page would redirect home.
  const slug = location.pathname
    .replace(/^\/en/, "")
    .replace(/^\//, "")
    .replace(/\/$/, "");
  const page = getLandingForLang(slug, isEnglish);
  if (!page) {
    return <Redirect to={prefix || "/"} />;
  }

  const c = page[lang];
  const path = `/${page.slug}`; // Turkish path (root)
  const enPath = `/${page.enSlug}`; // English path (/en/...)
  const canonical = isEnglish
    ? `${BASE_URL}/en${enPath}/`
    : `${BASE_URL}${path}/`;
  const faq: LandingFaq[] = c.faq ?? [];

  // Service + FAQPage structured data (one @graph payload).
  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: c.serviceName,
        serviceType: c.serviceName,
        description: c.metaDescription,
        areaServed: "TR",
        url: canonical,
        provider: {
          "@type": "Organization",
          name: "Shift Tracker",
          url: `${BASE_URL}/`,
        },
      },
      {
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.question,
          acceptedAnswer: { "@type": "Answer", text: f.answer },
        })),
      },
    ],
  };

  // Internal links: every sibling landing page + fraud + TURPAK article + blog.
  const otherLandings = landingPages
    .filter((p) => p.slug !== page.slug)
    .map((p) => ({
      to: isEnglish ? `/en/${p.enSlug}/` : `/${p.slug}/`,
      label: p[lang].serviceName,
    }));
  const related: Array<{ to: string; label: string }> = [
    ...otherLandings,
    {
      to: `${prefix}/fraud-prevention/`,
      label: lang === "en" ? "Fraud detection & prevention" : "Suistimal önleme",
    },
    {
      to: isEnglish ? `/en/blog/${TURPAK_EN}/` : `/blog/${TURPAK_TR}/`,
      label:
        lang === "en"
          ? "TURPAK automation vs. bank reconciliation"
          : "TURPAK otomasyonu ve banka mutabakatı",
    },
    {
      to: `${prefix}/blog/`,
      label: "Blog",
    },
  ];

  const contactHref = `${prefix}/contact/`;

  return (
    <div className={styles.page}>
      <Seo
        path={path}
        enPath={enPath}
        title={c.metaTitle}
        description={c.metaDescription}
        schema={schema}
      />

      <section className={styles.hero}>
        <h1 className={styles.h1}>{c.h1}</h1>
        <p className={styles.heroSubtitle}>{c.heroSubtitle}</p>
        <p className={styles.intro}>{c.intro}</p>
        <NavLink to={contactHref} className={styles.heroCta}>
          {c.ctaButton}
        </NavLink>
      </section>

      {page.slug === "canli-satis-takibi" && <LiveSalesShowcase lang={lang} />}
      {page.slug === "tank-stok-takibi" && <TanksShowcase lang={lang} />}
      {page.slug === "akaryakit-siparis-onerisi" && (
        <OrderSuggestionsShowcase lang={lang} />
      )}

      <section className={styles.features}>
        <h2 className={styles.sectionTitle}>{c.featuresTitle}</h2>
        <div className={styles.featureGrid}>
          {c.features.map((f, i) => (
            <div key={i} className={styles.featureCard}>
              <div className={styles.featureIcon} aria-hidden="true">
                {f.icon}
              </div>
              <h3>{f.title}</h3>
              <p>{f.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className={styles.faq}>
        <h2 className={styles.sectionTitle}>{c.faqTitle}</h2>
        <div className={styles.faqList}>
          {faq.map((item, i) => (
            <details key={i} className={styles.faqItem}>
              <summary className={styles.faqQuestion}>{item.question}</summary>
              <p className={styles.faqAnswer}>{item.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <section className={styles.related}>
        <h2 className={styles.sectionTitle}>{UI[lang].related}</h2>
        <ul className={styles.relatedList}>
          {related.map((r, i) => (
            <li key={i}>
              <NavLink to={r.to}>{r.label}</NavLink>
            </li>
          ))}
        </ul>
      </section>

      <Testimonials lang={lang} />

      <section className={styles.cta}>
        <h2>{c.ctaTitle}</h2>
        <p>{c.ctaText}</p>
        <NavLink to={contactHref} className={styles.ctaButton}>
          {c.ctaButton}
        </NavLink>
      </section>
    </div>
  );
}

export default LandingPage;
